import { supabase } from '../../shared/supabase/supabaseClient';
import {
  REPORTS_REMOTE_CACHE_MS,
  reportsCacheKey,
  withReportsCache,
} from './reportsRemoteCache';
import { getSalesTimeBandWindows } from '../../sales/infra/salesTimeBandWindows';
import type { SalesByCashierRow, SalesByTimeBandRow, SalesComparison, SalesSnapshot } from '../../sales/infra/salesSnapshotTypes';
import type { ReportsQueryScope } from '../domain/reportsQueryScope';
import type { PosExceptionSummary, SalesDailyRow } from '../domain/reportsTypes';
import { mapRpcError, pickIso, rpcVentasProductFilters, scopeCacheKeyParts } from './reportsRemoteShared';

export async function fetchRemoteSalesSnapshot(
  branchId: string,
  fromIso: string,
  toIso: string,
  queryScope?: ReportsQueryScope | null,
  listFilters?: {
    statusFilter?: string | null;
    paymentMethodFilter?: string | null;
    fiscalStatusFilter?: string | null;
    channelFilter?: string | null;
    primaryStaffUserIdFilter?: string | null;
    paymentPendingOnly?: boolean;
    commissionFilter?: string | null;
  }
): Promise<SalesSnapshot | null> {
  const scopeRpc = rpcVentasProductFilters(queryScope);
  const statusFilter = listFilters?.statusFilter ?? null;
  const paymentMethodFilter = listFilters?.paymentMethodFilter ?? null;
  const fiscalStatusFilter = listFilters?.fiscalStatusFilter ?? null;
  const channelFilter = listFilters?.channelFilter ?? null;
  const primaryStaffUserIdFilter = listFilters?.primaryStaffUserIdFilter ?? null;
  const paymentPendingOnly = listFilters?.paymentPendingOnly === true;
  const commissionFilter = listFilters?.commissionFilter ?? null;
  return withReportsCache(
    reportsCacheKey([
      'reports_branch_sales_snapshot',
      branchId,
      fromIso,
      toIso,
      ...scopeCacheKeyParts(scopeRpc),
      statusFilter,
      paymentMethodFilter,
      fiscalStatusFilter,
      channelFilter,
      primaryStaffUserIdFilter,
      paymentPendingOnly ? 1 : 0,
      commissionFilter,
    ]),
    async () => {
      const { data, error } = await supabase.rpc('reports_branch_sales_snapshot', {
        p_branch_id: branchId,
        p_from: fromIso,
        p_to: toIso,
        ...scopeRpc,
        p_status_filter: statusFilter,
        p_payment_method_filter: paymentMethodFilter,
        p_fiscal_status_filter: fiscalStatusFilter,
        p_channel_filter: channelFilter,
        p_primary_staff_user_id: primaryStaffUserIdFilter,
        p_payment_pending_only: paymentPendingOnly,
        p_commission_filter: commissionFilter,
      });
      if (error) {
        console.warn('[reportsRemote] reports_branch_sales_snapshot', mapRpcError(error));
        return null;
      }
      const j = data as Record<string, unknown> | null;
      if (!j) return null;

      const breakdownRaw = j.payment_breakdown;
      const paymentBreakdown = Array.isArray(breakdownRaw)
        ? (breakdownRaw as { method_id?: string; total?: number }[]).map((r) => ({
            methodId: String(r.method_id ?? ''),
            total: Number(r.total ?? 0),
          }))
        : [];

      return {
        salesCount: Number(j.sales_count ?? 0),
        salesTotal: Number(j.sales_total ?? 0),
        fiscalSalesCount: Number(j.fiscal_sales_count ?? 0),
        fiscalSalesTotal: Number(j.fiscal_sales_total ?? 0),
        nonFiscalSalesCount: Number(j.non_fiscal_sales_count ?? 0),
        nonFiscalSalesTotal: Number(j.non_fiscal_sales_total ?? 0),
        pendingPayments: Number(j.pending_payments ?? 0),
        pendingPaymentsAmount:
          j.pending_payments_amount !== undefined && j.pending_payments_amount !== null
            ? Number(j.pending_payments_amount)
            : undefined,
        paymentBreakdown,
        discountTotal:
          j.discount_total !== undefined && j.discount_total !== null
            ? Number(j.discount_total)
            : undefined,
        subtotalBeforeDiscount:
          j.subtotal_before_discount !== undefined && j.subtotal_before_discount !== null
            ? Number(j.subtotal_before_discount)
            : undefined,
        salesWithDiscountCount:
          j.sales_with_discount_count !== undefined && j.sales_with_discount_count !== null
            ? Number(j.sales_with_discount_count)
            : undefined,
        voidedCount:
          j.voided_count !== undefined && j.voided_count !== null
            ? Number(j.voided_count)
            : undefined,
        activeCount:
          j.active_count !== undefined && j.active_count !== null
            ? Number(j.active_count)
            : undefined,
        paidAmount:
          j.paid_amount !== undefined && j.paid_amount !== null
            ? Number(j.paid_amount)
            : undefined,
      };
    },
    REPORTS_REMOTE_CACHE_MS,
    { category: 'reports', name: 'reports_branch_sales_snapshot' }
  );
}

export async function fetchRemotePosExceptionSummary(
  branchId: string,
  fromIso: string,
  toIso: string
): Promise<PosExceptionSummary | null> {
  return withReportsCache(
    reportsCacheKey(['reports_branch_pos_exception_summary', branchId, fromIso, toIso]),
    async () => {
      const { data, error } = await supabase.rpc('reports_branch_pos_exception_summary', {
        p_branch_id: branchId,
        p_from: fromIso,
        p_to: toIso,
      });
      if (error) {
        console.warn('[reportsRemote] reports_branch_pos_exception_summary', mapRpcError(error));
        return null;
      }
      const j = data as Record<string, unknown> | null;
      if (!j) return null;
      return {
        paymentPendingCount: Number(j.payment_pending_count ?? 0),
        paymentPendingAmount: Number(j.payment_pending_amount ?? 0),
        fiscalPendingCount: Number(j.fiscal_pending_count ?? 0),
        fiscalPendingAmount: Number(j.fiscal_pending_amount ?? 0),
        syncFailedCount: Number(j.sync_failed_count ?? 0),
        syncPendingCount: Number(j.sync_pending_count ?? 0),
        mpQrPendingCount: Number(j.mp_qr_pending_count ?? 0),
        mpQrPendingAmount: Number(j.mp_qr_pending_amount ?? 0),
        mpPosConfigured: j.mp_pos_configured === true,
        mpReconciliationReady: j.mp_reconciliation_ready === true,
        oldestPendingSaleAt: j.oldest_pending_sale_at != null ? String(j.oldest_pending_sale_at) : null,
        openCashSessions: Number(j.open_cash_sessions ?? 0),
      };
    },
    REPORTS_REMOTE_CACHE_MS,
    { category: 'reports', name: 'reports_branch_pos_exception_summary' }
  );
}

/** Serie diaria de ventas (RPC `reports_branch_sales_daily`). */
export async function fetchRemoteSalesDaily(
  branchId: string,
  fromIso: string,
  toIso: string,
  queryScope?: ReportsQueryScope | null
): Promise<SalesDailyRow[] | null> {
  const scopeRpc = rpcVentasProductFilters(queryScope);
  return withReportsCache(
    reportsCacheKey([
      'reports_branch_sales_daily',
      branchId,
      fromIso,
      toIso,
      ...scopeCacheKeyParts(scopeRpc),
    ]),
    async () => {
      const { data, error } = await supabase.rpc('reports_branch_sales_daily', {
        p_branch_id: branchId,
        p_from: fromIso,
        p_to: toIso,
        ...scopeRpc,
      });
      if (error) {
        console.warn('[reportsRemote] reports_branch_sales_daily', mapRpcError(error));
        return null;
      }
      if (!Array.isArray(data)) return null;
      return (data as { day?: string; sales_count?: number; sales_total?: number }[]).map((r) => ({
        day: String(r.day ?? ''),
        salesCount: Number(r.sales_count ?? 0),
        salesTotal: Number(r.sales_total ?? 0),
      }));
    },
    REPORTS_REMOTE_CACHE_MS,
    { category: 'reports', name: 'reports_branch_sales_daily' }
  );
}

export async function fetchRemoteSalesRangeTotals(
  branchId: string,
  fromIso: string,
  toIso: string,
  queryScope?: ReportsQueryScope | null
): Promise<{ count: number; total: number } | null> {
  const scopeRpc = rpcVentasProductFilters(queryScope);
  return withReportsCache(
    reportsCacheKey([
      'reports_branch_sales_range_totals',
      branchId,
      fromIso,
      toIso,
      ...scopeCacheKeyParts(scopeRpc),
    ]),
    async () => {
      const { data, error } = await supabase.rpc('reports_branch_sales_range_totals', {
        p_branch_id: branchId,
        p_from: fromIso,
        p_to: toIso,
        ...scopeRpc,
      });
      if (error) {
        console.warn('[reportsRemote] reports_branch_sales_range_totals', mapRpcError(error));
        return null;
      }
      const j = data as { count?: number; total?: number } | null;
      if (!j) return null;
      return { count: Number(j.count ?? 0), total: Number(j.total ?? 0) };
    },
    REPORTS_REMOTE_CACHE_MS,
    { category: 'reports', name: 'reports_branch_sales_range_totals' }
  );
}

/**
 * Batch de `reports_branch_sales_range_totals`: una sola request para N rangos
 * (franjas horarias + comparación). Devuelve las filas en el orden de entrada.
 */
async function fetchRemoteSalesRangesTotals(
  branchId: string,
  ranges: { from: string; to: string }[],
  queryScope?: ReportsQueryScope | null
): Promise<{ count: number; total: number }[] | null> {
  const scopeRpc = rpcVentasProductFilters(queryScope);
  const { data, error } = await supabase.rpc('reports_branch_sales_ranges_totals', {
    p_branch_id: branchId,
    p_ranges: ranges,
    ...scopeRpc,
  });
  if (error) {
    console.warn('[reportsRemote] reports_branch_sales_ranges_totals', mapRpcError(error));
    return null;
  }
  if (!Array.isArray(data)) return null;
  return (data as { count?: number; total?: number }[]).map((r) => ({
    count: Number(r?.count ?? 0),
    total: Number(r?.total ?? 0),
  }));
}

export async function fetchRemoteSalesTimeBands(
  branchId: string,
  dateForBandsIso: string,
  queryScope?: ReportsQueryScope | null
): Promise<SalesByTimeBandRow[] | null> {
  const bands = getSalesTimeBandWindows(dateForBandsIso);
  const scopeRpc = rpcVentasProductFilters(queryScope);
  return withReportsCache(
    reportsCacheKey([
      'reports_sales_time_bands',
      branchId,
      dateForBandsIso,
      ...scopeCacheKeyParts(scopeRpc),
    ]),
    async () => {
      const rows = await fetchRemoteSalesRangesTotals(
        branchId,
        bands.map((b) => ({ from: b.start, to: b.end })),
        queryScope
      );
      if (!rows || rows.length < bands.length) return null;
      return bands.map(
        (b, i) =>
          ({
            band: b.key,
            label: b.label,
            count: rows[i].count,
            total: rows[i].total,
          }) satisfies SalesByTimeBandRow
      );
    },
    REPORTS_REMOTE_CACHE_MS,
    { category: 'reports', name: 'reports_branch_sales_ranges_totals' }
  );
}

export async function fetchRemoteSalesComparison(branchId: string): Promise<SalesComparison | null> {
  const now = new Date();
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);
  const yesterdayStart = new Date(now);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  yesterdayStart.setHours(0, 0, 0, 0);
  const yesterdayEnd = new Date(yesterdayStart);
  yesterdayEnd.setHours(23, 59, 59, 999);
  const lastWeekStart = new Date(now);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);
  lastWeekStart.setHours(0, 0, 0, 0);
  const lastWeekEnd = new Date(lastWeekStart);
  lastWeekEnd.setHours(23, 59, 59, 999);

  return withReportsCache(
    reportsCacheKey(['reports_sales_comparison', branchId, todayStart.toISOString()]),
    async () => {
      const rows = await fetchRemoteSalesRangesTotals(branchId, [
        { from: todayStart.toISOString(), to: todayEnd.toISOString() },
        { from: yesterdayStart.toISOString(), to: yesterdayEnd.toISOString() },
        { from: lastWeekStart.toISOString(), to: lastWeekEnd.toISOString() },
      ]);
      if (!rows || rows.length < 3) return null;
      const [today, yesterday, lastWeek] = rows;
      return {
        todayTotal: today.total,
        todayCount: today.count,
        yesterdayTotal: yesterday.total,
        yesterdayCount: yesterday.count,
        sameDayLastWeekTotal: lastWeek.total,
        sameDayLastWeekCount: lastWeek.count,
      } satisfies SalesComparison;
    },
    REPORTS_REMOTE_CACHE_MS,
    { category: 'reports', name: 'reports_branch_sales_ranges_totals' }
  );
}

export async function fetchRemoteSalesByCashier(
  branchId: string,
  fromIso: string,
  toIso: string,
  queryScope?: ReportsQueryScope | null
): Promise<SalesByCashierRow[] | null> {
  const scopeRpc = rpcVentasProductFilters(queryScope);
  return withReportsCache(
    reportsCacheKey([
      'reports_branch_sales_by_cashier',
      branchId,
      fromIso,
      toIso,
      ...scopeCacheKeyParts(scopeRpc),
    ]),
    async () => {
      const { data, error } = await supabase.rpc('reports_branch_sales_by_cashier', {
        p_branch_id: branchId,
        p_from: fromIso,
        p_to: toIso,
        ...scopeRpc,
      });
      if (error) {
        console.warn('[reportsRemote] reports_branch_sales_by_cashier', mapRpcError(error));
        return null;
      }
      if (!Array.isArray(data)) return null;
      return (data as Record<string, unknown>[]).map((r) => {
        const rec = r as Record<string, unknown>;
        const salesTotal = Number(r.sales_total ?? 0);
        const salesCount = Number(r.sales_count ?? 0);
        return {
          cashierUserId: r.cashier_user_id != null ? String(r.cashier_user_id) : null,
          displayName: String(r.display_name ?? 'Sin usuario'),
          isOwner: Boolean(r.is_owner),
          salesCount,
          salesTotal,
          ticketAverage: Number(r.ticket_average ?? (salesCount > 0 ? salesTotal / salesCount : 0)),
          pendingPayments: Number(r.pending_payments ?? 0),
          fiscalSalesCount: Number(r.fiscal_sales_count ?? 0),
          nonFiscalSalesCount: Number(r.non_fiscal_sales_count ?? 0),
          margenReposicionTotal: Number(r.margen_reposicion_total ?? 0),
          sharePct: Number(r.share_pct ?? 0),
          firstSaleAt: pickIso(rec, 'first_sale_at', 'firstSaleAt'),
          lastSaleAt: pickIso(rec, 'last_sale_at', 'lastSaleAt'),
        } satisfies SalesByCashierRow;
      });
    },
    REPORTS_REMOTE_CACHE_MS,
    { category: 'reports', name: 'reports_branch_sales_by_cashier' }
  );
}
